import React from 'react';
import PillarLayout from './PillarLayout';
import { SOCIAL_IMAGE } from '../../seo';

const P = ({ children }) => <p className="mb-4">{children}</p>;
const UL = ({ children }) => <ul className="mb-4 pl-5 list-disc space-y-1">{children}</ul>;
const Q = ({ children }) => (
  <blockquote className="my-5 pl-4" style={{ borderLeft: '3px solid var(--nb-accent)', fontFamily: 'var(--script)', color: 'var(--ink)', fontSize: 'var(--t-md)', lineHeight: 1.3 }}>
    {children}
  </blockquote>
);

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'IADT verification, applied to hardware teams',
  description: "IADT — Inspection, Analysis, Demonstration, Test — is the verification framework from MIL-STD-961 and adopted by AS9100, FDA design controls, and program reviews across defense and aerospace. Tagging it at requirement-write time changes how requirements are written, and gives the engineering team a defensible record by default.",
  author: { '@type': 'Organization', name: 'Factory OS' },
  publisher: { '@type': 'Organization', name: 'Factory OS', url: 'https://factory-os.com', logo: { '@type': 'ImageObject', url: 'https://factory-os.com/og-image.png' } },
  datePublished: '2026-07-01',
  dateModified: '2026-07-24',
  image: SOCIAL_IMAGE,
  mainEntityOfPage: 'https://factory-os.com/resources/iadt-verification-for-hardware-teams',
  about: ['IADT verification', 'Requirements verification', 'MIL-STD-961', 'AS9100', 'Hardware verification', 'Program review'],
};

const IadtVerificationForHardware = () => (
  <PillarLayout
    meta={{
      title: 'IADT verification, applied to hardware teams | Factory OS',
      description: "IADT — Inspection, Analysis, Demonstration, Test — is the verification framework defense and aerospace program reviews already speak. Tagging it at requirement-write time changes how requirements are written. Here's the framework, the four methods, and what changes when you apply it.",
      path: '/resources/iadt-verification-for-hardware-teams',
    }}
    hero={{
      eyebrow: 'the engineering operating system · verification rigor',
      h1: 'IADT verification, applied to hardware teams.',
      thesis: "Requirements without a verification method aren't requirements. They're hopes.",
    }}
    articleSchema={articleSchema}
    related={[
      { href: '/resources/what-is-an-engineering-operating-system', title: 'What is an Engineering Operating System?' },
      { href: '/resources/design-evidence-as-byproduct', title: 'Design evidence should be a byproduct, not a deliverable' },
      { href: '/resources/engineering-operating-system-vs-plm', title: 'Engineering Operating System vs. PLM' },
    ]}
    sections={[
      {
        heading: 'What IADT stands for',
        body: (
          <>
            <P>
              <b>IADT</b> is the four-method verification framework codified in MIL-STD-961 and adopted across defense and aerospace program management, AS9100, FDA design controls, and most serious systems-engineering practice. Every requirement gets exactly one of the four methods. The four are:
            </P>
            <UL>
              <li><b>Inspection (I).</b> Verify by direct measurement with a tool — calipers, a scale, a micrometer, an optical comparator, a CMM. Used when the requirement is geometric or otherwise directly measurable on the as-built part.</li>
              <li><b>Analysis (A).</b> Verify by mathematical or computational argument — FEA, CFD, hand calc, statistical inference, simulation. Used when measuring the requirement on the as-built part would be impractical or destructive, and the math can stand in.</li>
              <li><b>Demonstration (D).</b> Verify by exercising the system in normal operation and showing it behaves correctly. Used when the requirement is about function, not number — a door opens, a release fires, a sensor reads.</li>
              <li><b>Test (T).</b> Verify by instrumenting and stressing the system, capturing measured response under a defined condition. Used for performance, durability, and stress requirements where the act of measuring requires controlled inputs.</li>
            </UL>
          </>
        ),
      },
      {
        heading: 'Why tagging verification at write-time matters',
        body: (
          <>
            <P>
              The naive way to write a requirement is to write what the part should do — "shall be lightweight," "shall withstand vibration," "shall pass thermal cycling." The verification method gets figured out later, often by whoever's running the test campaign, often weeks after the requirement was set.
            </P>
            <P>
              This is where a surprisingly large fraction of program risk hides. Three things happen when verification is deferred:
            </P>
            <UL>
              <li><b>The same requirement gets verified three ways by three engineers.</b> One does an analysis, one does a test, one inspects on the bench. Two of the three are wasted effort, and nobody agrees on which one counts.</li>
              <li><b>Some requirements don't get verified at all.</b> Because no engineer owned the verification method at write-time, no engineer owned the test plan at run-time. The program review finds the gap.</li>
              <li><b>Requirements get written in shapes that can't be verified.</b> "Shall be reliable" looks fine in a document. It fails the first time anyone asks how to test it.</li>
            </UL>
            <Q>
              Tagging IADT at write-time forces the writer to think about how the requirement gets proved — which is the only honest way to know if it's actually a requirement.
            </Q>
            <P>
              "Shall be lightweight" becomes "shall weigh ≤ 2.4 kg, verified by Inspection." "Shall withstand vibration" becomes "shall survive a 6G random vibration profile per MIL-STD-810G Method 514.6 without functional failure, verified by Test." The act of choosing the method changes the shape of the requirement.
            </P>
          </>
        ),
      },
      {
        heading: 'A worked example',
        body: (
          <>
            <P>
              Take a front A-arm on a competition vehicle suspension. Naive requirement:
            </P>
            <Q>
              REQ-014 · The front A-arm shall be strong enough.
            </Q>
            <P>
              That's not a requirement. There's no number, no method, no way to know when it's met. A program review would mark it open.
            </P>
            <P>
              Re-written with IADT tagged at write-time:
            </P>
            <Q>
              REQ-014 · The front A-arm shall withstand 6G impulse loading without yielding, verified by Test. Target value: 0.0 mm permanent deformation. Tolerance: ±0.1 mm.
            </Q>
            <P>
              The verification method (T) implies a test plan: instrument the A-arm, apply a 6G impulse via drop fixture or hydraulic cylinder, measure permanent deformation with a dial indicator. The test plan can be generated from the requirement; the engineer running the test doesn't have to interpret what was wanted.
            </P>
            <P>
              When the test runs, the measured value (say, "334 MPa peak stress, 0.05 mm permanent set") attaches to REQ-014. Pass/fail is computed automatically against the target and tolerance. The verification matrix updates because the requirement and the test result share a foreign key, not because someone updated a spreadsheet.
            </P>
          </>
        ),
      },
      {
        heading: 'Why this maps cleanly onto an Engineering Operating System',
        body: (
          <>
            <P>
              IADT is the example par excellence of the <a href="/resources/design-evidence-as-byproduct" style={{ color: 'var(--nb-accent)' }}>evidence-as-byproduct principle</a>. The framework predates the Engineering Operating System by decades — it's been in MIL-STD-961 since the 1990s and in serious systems engineering practice since before that. What an Engineering Operating System adds is automation:
            </P>
            <UL>
              <li>The verification method is required at requirement write-time. The system won't accept a requirement without it.</li>
              <li>The implied test plan is generated from the requirement plus the chosen method.</li>
              <li>Test results attach to requirements by foreign key, not by manual association.</li>
              <li>Pass/fail is computed against the target value and tolerance, not interpreted.</li>
              <li>The verification matrix is a view over the requirement and test-result tables. It exists at all times, not assembled before a review.</li>
              <li>Failures open new revisions with the failing requirement cited, so the next revision starts with the open gap visible.</li>
            </UL>
            <P>
              None of these are tricks. They're what happens when the verification framework an engineering team already wants to use is built into the system that runs the work.
            </P>
          </>
        ),
      },
      {
        heading: 'Where IADT shows up outside of MIL-STD',
        body: (
          <>
            <P>
              IADT vocabulary is most explicit in defense and aerospace, but the four methods show up across regulated industries under slightly different names. A few examples:
            </P>
            <UL>
              <li><b>AS9100 / aerospace quality.</b> Uses the four methods directly under verification planning.</li>
              <li><b>FDA Design Controls (21 CFR 820.30).</b> Requires "verification" and "validation" with documented methods — typically mapped to a subset of IADT plus user-needs validation on top.</li>
              <li><b>Automotive (IATF 16949, ASPICE).</b> Verification-by-method language aligned with IADT-equivalent categories.</li>
              <li><b>NASA NPR 7150.</b> Systems engineering verification with explicit method tagging.</li>
              <li><b>ISO 26262 (functional safety).</b> Verification rigor by ASIL with method-of-verification per requirement.</li>
            </UL>
            <P>
              A hardware team that adopts IADT internally — even outside a regulated context — gives itself a vocabulary that any of the above environments already speaks. Which means when a customer audit, a partner due-diligence, or an acquirer's technical review arrives, the engineering record is already in a language the reviewer knows.
            </P>
          </>
        ),
      },
      {
        heading: 'The bottom line',
        body: (
          <>
            <P>
              IADT is not new and Factory OS didn't invent it. What's new is the assumption that an Engineering Operating System should make IADT non-optional at requirement-write time, generate the implied test plan automatically, and bind test results to requirements as a property of the data model. When that's done:
            </P>
            <Q>
              The verification matrix exists because the engineering happened. Not because someone built it for the review.
            </Q>
            <P>
              That's the test of whether you've adopted IADT seriously. Either the matrix is a byproduct of the work, or it's a deliverable that competes with the work. Pick one.
            </P>
          </>
        ),
      },
    ]}
  />
);

export default IadtVerificationForHardware;
